import React, { useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { ProductContent } from "../../../data/productData";
import { ProductOptionsTab } from "../../components/ProductOptionsTab";
import { ProductMarketingSections } from "./ProductMarketingSections";
import { OptionsCategorySection } from "../../components/options/OptionsCategorySection";
import { useInViewOnce } from "../../hooks/useInViewOnce";
import { cn } from "../../components/ui/utils";
import { HEADLINE_GRADIENT } from "../../components/GradientSectionHeading";
import { FluidCanvas } from "../../components/FluidCanvas";

function AccordionItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="w-full border-b border-[#131312]/10 transition-all duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-start justify-between text-left group"
      >
        <span className="text-xl font-normal text-[#131312] group-hover:opacity-70 transition-opacity pr-8">
          {question}
        </span>
        <div className="flex-shrink-0 text-[#131312]/40 group-hover:text-[#131312] transition-colors mt-1.5">
          {isOpen ? <Minus className="size-5" /> : <Plus className="size-5" />}
        </div>
      </button>
      <div className={cn(
        "overflow-hidden transition-all duration-500 ease-in-out",
        isOpen ? "max-h-[500px] pb-5 opacity-100" : "max-h-0 opacity-0"
      )}>
        <p className="text-[#131312]/60 leading-relaxed text-lg w-full">{answer}</p>
      </div>
    </div>
  );
}

interface UnifiedProductPageProps {
  content: ProductContent;
}

const SLUG_IMAGE: Record<string, string> = {
  "textile-home-packaging": "/products/textile.jpeg",
  "garment-packaging": "/products/garment.jpeg",
  "luxury-packaging": "/products/luxury.jpeg",
  "labels-stickers": "/products/label.jpeg",
  "custom-packaging": "/products/custom.jpeg",
  "retail-display-packaging": "/products/retail.jpeg",
  "eco-friendly-packaging": "/products/eco.jpeg",
  "business-print-materials": "/products/business.jpeg",
};

export function UnifiedProductPage({ content }: UnifiedProductPageProps) {
  const { slug = "" } = useParams<{ slug: string }>();
  const storyImage = SLUG_IMAGE[slug] ?? "/story.png";
  const { ref: headlineRef, inView: headlineInView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });
  return (
    <div className="min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* HERO SECTION */}
      <section className="relative py-32 bg-[#F6F2EB] overflow-hidden min-h-[768px] flex items-center border-b border-[#131312]/5">
        <FluidCanvas />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            {content.badge && (
              <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#FBF7F4] text-[#131312] border border-[#131312]/10 rounded-full text-sm font-medium mb-8 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.15)]">
                {content.badge}
              </div>
            )}
            <h1 ref={headlineRef} className="font-bold mb-8 leading-tight text-[#1C1C1C]" style={{ fontSize: 'clamp(32px, 6.5vw, 78.1px)' }}>
              <span
                className={cn("gradient-text-animate inline", headlineInView && "gradient-text-animate--play")}
                style={{ backgroundImage: HEADLINE_GRADIENT }}
              >
                {content.title}
              </span>
            </h1>
            <p
              className="font-bold leading-[25px] text-[#1C1C1C] mx-auto"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '18.6px',
                maxWidth: '627px',
                letterSpacing: '-0.5px'
              }}
            >
              {content.description}
            </p>
          </div>
        </div>
      </section>

      {/* OUR STORY SECTION */}
      <section className="py-24 bg-background overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:items-center max-w-6xl mx-auto">
            <div
              className="relative order-2 lg:order-1 overflow-hidden w-full"
              style={{ height: "auto", aspectRatio: "504/443", background: "#F3F4F6", borderRadius: "16px", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
            >
              <img src={storyImage} alt={content.title} className="h-full w-full object-cover" />
            </div>
            <div className="flex flex-col items-start order-1 lg:order-2 w-full" style={{ gap: "24px" }}>
              <h2 style={{ width: "100%", fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 5vw, 40px)", lineHeight: "1.1", letterSpacing: "-1px", margin: 0 }}>
                <span className="gradient-text-animate gradient-text-animate--play" style={{ backgroundImage: HEADLINE_GRADIENT }}>
                  {content.story?.heading ?? "Our Story"}
                </span>
              </h2>
              <div className="flex flex-col items-start" style={{ gap: "24px", width: "100%" }}>
                {(content.story?.paragraphs ?? [
                  "Founded in 1995 as Paper Printing Services, we began our journey with a simple mission: to deliver exceptional quality in every print. Over three decades, we've evolved into a global packaging leader, trusted by some of the world's most prestigious brands.",
                  "Today, we specialize in premium packaging solutions for textile, fashion, cosmetics, and e-commerce industries. Our commitment to sustainability and quality is reflected in our FSC, ISO 9001, and Sedex certifications.",
                  "With state-of-the-art facilities spanning 22,500 square feet and production running Monday to Saturday, 9 AM to 6 PM, we produce over 50 million units annually while maintaining our founding principle: unwavering dedication to excellence."
                ]).map((para, i) => (
                  <p key={i} className="text-[#131312]/70" style={{ fontFamily: "'Manrope', sans-serif", fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OPTIONAL: BOX TYPES */}
      {content.showBoxTypesFirst && (
        <OptionsCategorySection
          id="box-types"
          categoryId="boxTypes"
          heading="Box types"
          subheading="Closures, trays, lids, and features — the same illustrated catalog as our options library, organised for this category."
        />
      )}

      {/* USE CASES SECTION */}
      {content.useCases && content.useCases.length > 0 && (
        <section className="py-24 bg-[#F6F2EB]">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-center md:text-left">{content.useCasesHeading ?? "Where it fits"}</h2>
            {content.useCasesSubheading && (
              <p className="text-[#131312]/70 text-base mb-12 text-center md:text-left">{content.useCasesSubheading}</p>
            )}
            {!content.useCasesSubheading && <div className="mb-12" />}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.useCases.map((u) => (
                <div 
                  key={u.title} 
                  className="flex flex-col items-start bg-[#FBFAF7] border border-black/10 rounded-[10px] transition-all hover:shadow-lg group"
                  style={{ padding: "25px 25px 20px" }}
                >
                  <h3 className="text-xl font-bold text-[#131312] mb-3">
                    {u.title}
                  </h3>
                  <p className="text-[#131312]/70 leading-relaxed flex-1">
                    {u.body}
                  </p>
                  {u.useCase && (
                    <p className="text-sm text-[#131312]/70 leading-relaxed mt-auto pt-4">
                      <span className="font-bold text-[#131312]">Use case: </span>{u.useCase}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {content.useCasesFootnote && (
              <p className="text-[#131312]/70 text-base mt-10 text-center md:text-left">{content.useCasesFootnote}</p>
            )}
          </div>
        </section>
      )}

      {/* MARKETING SECTION */}
      {content.marketingSection && (
        <ProductMarketingSections
          sizesTitle={content.marketingSection.sizesTitle}
          sizesDescription={content.marketingSection.sizesDescription}
          ctaLabel={content.marketingSection.ctaLabel}
          industryHeading={content.industrySection?.heading}
          industrySubheading={content.industrySection?.subheading}
          industries={content.industrySection?.industries}
        />
      )}

      {/* INSIDE OUR FACILITY */}
      <section className="py-20 bg-[#F6F2EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-[#FBFAF7] border border-[#131312]/10 rounded-full text-sm font-medium text-[#131312] mb-4 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.15)]">
              Gallery
            </div>
            <h2 className="text-4xl font-bold mb-4 text-[#131312]">Product Gallery</h2>
            <p className="text-lg text-[#131312]/70 max-w-3xl mx-auto">
              Real examples of our craftsmanship and quality
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { src: "/product gallery/banana road.jpeg", alt: "Banana Road packaging", title: "Banana Road", sub: "Body Mist Packaging" },
              { src: "/product gallery/curex.jpeg", alt: "Curex packaging", title: "Curex", sub: "Skincare Packaging" },
              { src: "/product gallery/diamondhome.jpeg", alt: "Diamond Home packaging", title: "Diamond Home", sub: "Candle Box" },
              { src: "/product gallery/lama.jpeg", alt: "Lama packaging", title: "Lama", sub: "Perfume Packaging" },
              { src: "/product gallery/orra.jpeg", alt: "Orra packaging", title: "Orra", sub: "Perfume Packaging" },
              { src: "/product gallery/the shoppe 2.jpeg", alt: "The Shoppe packaging bag", title: "The Shoppe Bag", sub: "Perfume Packaging" },
              { src: "/product gallery/the shoppe.jpeg", alt: "The Shoppe packaging box", title: "The Shoppe Box", sub: "Perfume Packaging" },
              { src: "/product gallery/urbansole.jpeg", alt: "Urban Sole packaging", title: "Urban Sole", sub: "Perfume Packaging" },
              { src: "/product gallery/vanci.jpeg", alt: "Vanci packaging", title: "Vanci", sub: "Perfume Packaging" },
            ].map(({ src, alt, title, sub }, index) => (
              <div key={index} className="relative rounded-2xl overflow-hidden aspect-[4/3] group">
                <img src={src} alt={alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="font-bold">{title}</div>
                  <div className="text-sm text-white/80">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT OPTIONS */}
      <ProductOptionsTab categoryIds={content.optionsCategoryIds} />

      {/* FAQ SECTION */}
      <section className="py-32 bg-[#F7F4ED]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
              <h2 className="text-4xl font-semibold leading-[1.1] tracking-tight text-[#131312] mb-8">
                Frequently Asked <br className="hidden lg:block" /> Questions
              </h2>
              <p className="text-[#131312]/70 text-lg max-w-sm">
                Can't find what you're looking for? Reach out to our team for personalized assistance.
              </p>
            </div>
            <div className="flex flex-col items-start p-0 w-full lg:max-w-[586px] flex-grow self-stretch">
              <AccordionItem
                question="What's your minimum order quantity?"
                answer="MOQs vary by product type. Contact us for specific requirements for your project."
              />
              <AccordionItem
                question="Do you ship internationally?"
                answer="Yes, we ship to UK, EU, USA, and other international markets."
              />
              <AccordionItem
                question="How long does production take?"
                answer="Typically 10-15 days after sample approval, depending on order complexity."
              />
              <AccordionItem
                question="Can I get physical samples?"
                answer="Yes, we provide physical samples before production at no additional cost."
              />
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
